import { Field } from '@web/components/atoms/Field'
import { Input } from '@web/components/atoms/Input'
import { HStack, VStack } from '@web/components/atoms/Layout'
import { Section } from '@web/components/atoms/Typography'
import { PageButton } from '../PageButton'
import { useVirtualGiftSection } from './VirtualGiftSectionProvider'
import { useMemo, useState } from 'react'
import { VirtualGiftSectionPreviewImageItem } from './VirtualGiftSectionPreviewImage'
import { v4 } from 'uuid'

type VirtualGiftSectionPersonalisationItemProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemOptions: any
  itemName: string
  index: number
  onFinalise?: () => Promise<boolean>
}

const VirtualGiftSectionPersonalisationItem = ({
  itemOptions,
  itemName,
  index,
  onFinalise,
}: VirtualGiftSectionPersonalisationItemProps) => {
  const current = index + 1
  const context = useVirtualGiftSection()
  const currentItem = context.items[index]
  const isFinalItem = context.quantity === current
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const id = useMemo(() => v4(), [])

  return (
    <HStack w="100%" gap="24px" id={`virtual-gift-personalisation-${current}`} flexDirection={['column', 'row']}>
      {itemOptions?.imageUrl && <VirtualGiftSectionPreviewImageItem id={id} itemOptions={itemOptions} index={index} />}
      <VStack w="100%" alignItems="start" gap="16px">
        <Section>
          {itemName} #{current}
        </Section>
        {itemOptions?.inputs &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          itemOptions.inputs.map((input: any, inputIndex: number) => {
            const value = currentItem.value?.[inputIndex] || ''
            const maxCharacters = input.maxCharacters
            const isOverMaxCharacters = maxCharacters ? value.length > maxCharacters : false
            return (
              <Field
                key={`${input.name}-${inputIndex}`}
                label={input.name}
                invalid={!!error || isOverMaxCharacters}
                errorText={isOverMaxCharacters ? `Maximum ${maxCharacters} characters allowed` : error}
              >
                <Input
                  placeholder={`Enter ${input.name.toLowerCase()}`}
                  value={value}
                  onChange={(e) => {
                    setError('')
                    context.updateItemValue(index, e.target.value, inputIndex)
                  }}
                />
              </Field>
            )
          })}
        <PageButton
          name="Primary"
          isLoading={isFinalItem && isLoading}
          loadingText={'Generating images...'}
          buttonText={
            isFinalItem
              ? `Finalise your ${itemName}${context.quantity > 1 ? 's' : ''}`
              : `Go to next ${itemName.toLowerCase()}`
          }
          onClick={async () => {
            if (currentItem.value.some((x) => !x)) {
              setError('Please fill out all fields before proceeding')
              return
            }

            let emptyItemNumber = -1
            for (let i = 0; i < context.items.length; i++) {
              if (!context.items[i].value || context.items[i].value.some((x) => !x)) {
                emptyItemNumber = i + 1
                break
              }
            }

            if (isFinalItem && emptyItemNumber === -1) {
              if (onFinalise) {
                try {
                  setIsLoading(true)
                  await onFinalise()
                } catch {
                  // swallow error, onFinalise will handle
                  return
                } finally {
                  setIsLoading(false)
                }
              }
              context.setStep(2)
              return
            }

            document
              .getElementById(`virtual-gift-personalisation-${isFinalItem ? emptyItemNumber : current + 1}`)
              ?.scrollIntoView({ behavior: 'smooth' })
          }}
        />
      </VStack>
    </HStack>
  )
}

type VirtualGiftSectionPersonalisationProps = {
  onFinalise?: () => Promise<boolean>
}

const VirtualGiftSectionPersonalisation = ({ onFinalise }: VirtualGiftSectionPersonalisationProps) => {
  const { quantity, settings } = useVirtualGiftSection()

  return (
    <VStack w="100%" gap="48px">
      {Array.from({ length: quantity }).map((_, index) => {
        const itemOptions = settings.styles?.options?.items?.[index] || settings.styles?.options?.items?.[0]
        return (
          <VirtualGiftSectionPersonalisationItem
            key={index}
            index={index}
            onFinalise={onFinalise}
            itemName={settings.styles?.options?.name || ''}
            itemOptions={itemOptions}
          />
        )
      })}
    </VStack>
  )
}

export default VirtualGiftSectionPersonalisation
